import { Environment } from '../../types/config';
import { ApiService } from './Api';
import { UserService } from './UserService';

/**
 * Makes authenticated API calls
 */
class AuthenticatedApiService extends ApiService {
  protected userService: UserService;

  constructor(environment: Environment, userService: UserService) {
    super(environment);
    this.userService = userService;
    this._name = 'AuthedApi';
  }

  public hasAccessToken = () => this.userService !== undefined && this.userService.getToken() !== null;

  protected get defaultAuthHeaders(): RequestInit {
    return {
      ...this.defaultHeaders,
      headers: {
        ...this.defaultHeaders.headers,
        Authorization: `Bearer ${this.userService.getToken()}`,
      },
    };
  }

  protected generateHeadersFromRequestAndDefault(requestHeaders?: RequestInit) {
    let headers = {
      ...this.defaultAuthHeaders.headers,
    };

    if (requestHeaders) {
      headers = {
        ...headers,
        ...requestHeaders.headers,
      };
    }

    return headers;
  }

  protected send(url: string | URL, params: RequestInit): Promise<Response | null> {
    const host = url instanceof URL ? url.host : url;
    // if the url is NOT for api/app.bombbomb.com convert the authorization to a BB-JWT header.
    if (!/(app|api)\.bombbomb\.com/.test(host) && params.headers && 'Authorization' in params.headers) {
      const match = /^Bearer (.*)$/.exec(params.headers.Authorization);
      if (match) {
        const [, token] = match;
        delete params.headers.Authorization;
        params.headers['BB-JWT'] = token;
      }
    }

    return super.send(url, params);
  }
}

export type { AuthenticatedApiService };

const apiServices = new Map<Environment, AuthenticatedApiService>();
export function getAuthenticatedApiService(
  environment: Environment,
  userService: UserService,
  reset = false,
) {
  if (reset) {
    apiServices.clear();
  }

  if (apiServices.has(environment)) {
    return apiServices.get(environment) as AuthenticatedApiService;
  }

  apiServices.set(environment, new AuthenticatedApiService(environment, userService));
  return apiServices.get(environment) as AuthenticatedApiService;
}
